import { isEmpty, validateNumericField, validateDateField, validatePopulationField, validateMintageField } from './utils';
import { requiredFields, optionalFields } from './constants';

export const validateRow = (row, rowIndex, headers, mappings) => {
  const errors = [];
  const mappedData = {};

  // Check if row is empty (all cells are empty)
  if (!row || !row.some(cell => cell !== undefined && cell !== null && cell !== '')) {
    errors.push('Empty row');
    return { isValid: false, errors, data: null };
  }

  // Map and validate fields
  headers.forEach((header, colIndex) => {
    const fieldKey = mappings[header];
    if (!fieldKey) return; // Skip unmapped fields

    const value = row[colIndex];
    const field = [...requiredFields, ...optionalFields].find(f => f.key === fieldKey);
    if (!field) return; // Skip if field not found

    // Only validate required fields that have been mapped
    if (requiredFields.some(rf => rf.key === field.key)) {
      if (!value && value !== 0) {
        errors.push(`Missing required field "${field.label}" in column "${header}"`);
      } else if (typeof value === 'string' && value.trim() === '') {
        errors.push(`Empty value for required field "${field.label}" in column "${header}"`);
      }
    }

    // Basic value assignment with type checking
    if (value !== undefined && value !== null) {
      if (typeof value === 'string') {
        mappedData[fieldKey] = value.trim();
      } else {
        mappedData[fieldKey] = value;
      }
    }
  });


  // Second pass: Type validation and conversion
  for (const [fieldKey, value] of Object.entries(mappedData)) {
    if (isEmpty(value)) {
      mappedData[fieldKey] = null;
      continue;
    }

    switch (fieldKey) {
      case 'year':
        // Allow both numeric and string years
        if (typeof value === 'number') {
          if (value < -9999 || value > 9999) {
            errors.push(`Year must be between -9999 and 9999, got ${value}`);
          }
          mappedData[fieldKey] = value;
        } else {
          const yearStr = value.toString().trim();
          // Only validate length since year is stored as a string
          if (yearStr.length > 50) {
            errors.push(`Year string cannot exceed 50 characters, got "${yearStr}" (${yearStr.length} chars)`);
          }
          mappedData[fieldKey] = yearStr;
        }
        break;

      case 'purchase_price':
      case 'price_guide_value':
      case 'price':
      case 'transaction_fees':
      case 'shipping_cost':
      case 'grading_cost':
      case 'composition':
      case 'weight':
      case 'diameter':
      case 'list_price':
        mappedData[fieldKey] = validateNumericField(value, fieldKey, errors, rowIndex);
        break;

      case 'population_when_bought':
      case 'current_population':
        mappedData['population'] = validatePopulationField(value, errors);
        break;

      case 'pop_higher_when_bought':
      case 'current_pop_higher':
        mappedData['pop_higher'] = validatePopulationField(value, errors);
        break;

      case 'mintage':
        mappedData[fieldKey] = validateMintageField(value, errors);
        break;

      case 'purchase_date':
      case 'transaction_date':
        // Handle Excel date numbers (days since 1900)
        if (typeof value === 'number' || /^\d+$/.test(value)) {
          const days = parseInt(value);
          const date = new Date(Date.UTC(1899, 11, 30 + days)).toISOString();
          if (!isNaN(date.getTime())) {
            mappedData[fieldKey] = date.toISOString().split('T')[0];
            break;
          }
        }
        // If not an Excel date number, try standard date parsing
        mappedData[fieldKey] = validateDateField(value, fieldKey, errors, rowIndex);
        break;

      case 'cac_sticker':
        const cacValue = value ? value.toString().toLowerCase().trim() : 'none';
        if (!['green', 'gold', 'none'].includes(cacValue)) {
          errors.push(`Invalid CAC value "${value}". Must be one of: green, gold, none`);
          mappedData[fieldKey] = 'none';
        } else {
          mappedData[fieldKey] = cacValue;
        }
        break;

      case 'status':
        const statusValue = value ? value.toString().trim() : 'Inventory';
        const validStatuses = ['Inventory', 'Sold', 'In Grading', 'Pending'];
        if (!validStatuses.includes(statusValue)) {
          errors.push(`Invalid status "${statusValue}". Must be one of: ${validStatuses.join(', ')}`);
          mappedData[fieldKey] = 'Inventory'; // Default to Inventory
        } else {
          mappedData[fieldKey] = statusValue;
        }
        break;

      case 'grade':
        const gradeStr = value.toString().trim();
        if (gradeStr.length > 100) {
          errors.push(`Grade value too long: "${gradeStr}" (${gradeStr.length} chars, max 100)`);
        }
        mappedData[fieldKey] = gradeStr;
        break;

      case 'certification_number':
        const certStr = value.toString().trim();
        if (certStr.length > 100) {
          errors.push(`Certification number too long: "${certStr}" (${certStr.length} chars, max 100)`);
        }
        mappedData[fieldKey] = certStr;
        break;

      default:
        // For string fields, trim and validate length
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.length > 1000) { // General max length for string fields
            errors.push(`Value too long for field "${fieldKey}": ${trimmed.length} chars (max 1000)`);
          }
          mappedData[fieldKey] = trimmed;
        } else {
          mappedData[fieldKey] = value;
        }
    }
  }

  // Check if this is a sold coin
  const isSold = mappedData.status?.toLowerCase() === 'sold';
  
  // Remove transaction and client fields from coin data
  const transactionFields = [
    'price', 'transaction_date', 'transaction_fees', 'transaction_notes',
    'client_name', 'client_email', 'client_phone', 'client_address', 'client_notes'
  ];
  transactionFields.forEach(field => delete mappedData[field]);

  // Set default status to 'Inventory' if not provided
  if (!mappedData.status) {
    mappedData.status = 'Inventory';
  }

  return { 
    isValid: errors.length === 0, 
    errors, 
    data: mappedData,
    isSold
  };
};

export const validateTransactionData = (transactions, data) => {
  // Return validation errors only for coins that need manual transaction entry
  return transactions
    .filter(t => !t.fromSpreadsheet)
    .map(t => `Row ${t.rowIndex + 2}: Transaction details required for sold coin`);
};
